import './JxAnalogClock.css';

const RADIUS = 60;
const CENTER = RADIUS;
const NUMERAL_RADIUS = RADIUS - 14; // inset from the rim so digits don't clip

/**
 * Reads hour/minute/second of `date` as they appear in `timeZone`, independent of the
 * browser's local timezone. Uses Intl.DateTimeFormat — no date library needed.
 * @param {Date} date
 * @param {string} timeZone - IANA zone, e.g. 'Europe/Amsterdam'
 */
const _timeInZone = (date, timeZone) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(date);

    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    return { hours: get('hour') % 24, minutes: get('minute'), seconds: get('second') };
};

/**
 * Returns the [x, y] tip coordinates of a clock hand.
 * @param {number} angleDeg - angle in degrees, 0 = 12 o'clock, clockwise
 * @param {number} length - hand length in px from center
 */
const _handPoint = (angleDeg, length) => {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return [CENTER + length * Math.cos(rad), CENTER + length * Math.sin(rad)];
};

let _name = 'JxAnalogClock';

/**
 * JxAnalogClock — pure painter, no internal state/timer. Caller owns the ticking `now`
 * and passes it down, same separation as the rest of JxControls (controls render,
 * forms own state/timers).
 *
 * @param {Date} now
 * @param {string} [timeZone] - IANA zone, e.g. 'Europe/Amsterdam'. Defaults to the browser's local zone.
 */
export default function JxAnalogClock({ id, now, timeZone, showDigital = false }) {
    const { hours, minutes, seconds } = timeZone
        ? _timeInZone(now, timeZone)
        : { hours: now.getHours(), minutes: now.getMinutes(), seconds: now.getSeconds() };

    const getId = () => {
        return `${_name}_${id}`;
    };

    const pad = (n) => String(n).padStart(2, '0');

    const dayName = timeZone
        ? now.toLocaleDateString('en-GB', { timeZone, weekday: 'short' })
        : now.toLocaleDateString('en-GB', { weekday: 'short' });

    const hourAngle = (hours % 12) * 30 + minutes * 0.5; // 360/12 + drift per minute
    const minuteAngle = minutes * 6 + seconds * 0.1; // 360/60 + drift per second
    const secondAngle = seconds * 6; // 360/60

    const [hx, hy] = _handPoint(hourAngle, RADIUS * 0.5);
    const [mx, my] = _handPoint(minuteAngle, RADIUS * 0.75);
    const [sx, sy] = _handPoint(secondAngle, RADIUS * 0.85);

    return (
        <div className="jx-analog-clock-wrap">
            <svg id={getId()} className="jx-analog-clock" viewBox={`0 0 ${RADIUS * 2} ${RADIUS * 2}`}>
                <circle className="jx-analog-clock-face" cx={CENTER} cy={CENTER} r={RADIUS - 2} />

                {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => {
                    const [nx, ny] = _handPoint(n * 30, NUMERAL_RADIUS);
                    return (
                        <text key={n} className="jx-analog-clock-numeral" x={nx} y={ny}>
                            {n}
                        </text>
                    );
                })}

                <line className="jx-analog-clock-hand-hour" x1={CENTER} y1={CENTER} x2={hx} y2={hy} />
                <line className="jx-analog-clock-hand-minute" x1={CENTER} y1={CENTER} x2={mx} y2={my} />
                <line className="jx-analog-clock-hand-second" x1={CENTER} y1={CENTER} x2={sx} y2={sy} />
                <circle className="jx-analog-clock-pin" cx={CENTER} cy={CENTER} r={2.5} />
            </svg>

            {showDigital && (
                <span className="jx-analog-clock-digital">
                    {pad(hours)}:{pad(minutes)}:{pad(seconds)} - {dayName}
                </span>
            )}
        </div>
    );
}
